/** Incoming Player Radar — FPL profil pro hráče bez FPL historie. */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Broadcast as RadarIcon, Plus, Trash, PencilSimple, ArrowsClockwise, CaretDown, CaretUp, X } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { radarSmSearch, radarSmPlayer, radarParseProfile } from "../lib/lab-api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/radar`;

const LEAGUES = ["Premier League", "La Liga", "Bundesliga", "Serie A", "Ligue 1", "Championship",
    "Eredivisie", "Primeira Liga", "Belgian Pro League", "Scottish Premiership", "Süper Lig",
    "Brazil Série A", "Argentina Primera", "MLS", "Saudi Pro League", "Austrian Bundesliga",
    "Swiss Super League", "Jiná liga"];

const ACTION_STYLE = {
    "Draftable now": "bg-fa-mint text-black",
    "Pre-season watch": "bg-fa-yellow text-black",
    "First wildcard target": "bg-fa-sky text-black",
    "Trap risk": "bg-red-400 text-black",
    "Avoid until nailed": "border border-white/30 text-white/70",
};
const PRICE_STYLE = { "great value": "text-fa-mint", "fair price": "text-fa-yellow", "overpriced": "text-red-300" };
const PRICE_LABEL = { "great value": "skvělá hodnota", "fair price": "férová cena", "overpriced": "předražený" };
const ROLE_STYLE = { "GW1 draft": "text-fa-mint", "Bench rotation": "text-fa-yellow", "Watchlist": "text-white/50" };

const inputCls = "bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-full focus:outline-none focus:border-fa-mint placeholder:text-white/25";

const EMPTY = { name: "", club: "", natural_position: "", fpl_position: "MID", age: "", previous_league: "Championship",
    minutes: "", goals: "", assists: "", xg: "", xa: "", clean_sheets: "", def_actions90: "", aerials90: "", cards: "",
    fee_m: "", competition: "medium", expected_price: "", preseason_minutes: "", note: "" };

function Bar({ label, value, color = "bg-fa-mint" }) {
    return (
        <div>
            <div className="flex justify-between font-mono text-[9px] text-white/50"><span>{label}</span><span className="tabular-nums text-white/80">{value}</span></div>
            <div className="h-1.5 bg-white/10"><div className={`h-full ${color}`} style={{ width: `${value}%` }} /></div>
        </div>
    );
}

const Field = ({ label, k, f, set, type = "text", ph = "" }) => (
    <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block">
        <div>{label}</div>
        <input type={type} value={f[k] ?? ""} onChange={set(k)} placeholder={ph} className={inputCls} data-testid={`radar-f-${k}`} />
    </label>
);

function RadarForm({ initial, onDone, onCancel }) {
    const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
    const [busy, setBusy] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiNote, setAiNote] = useState(null);
    const [smBusy, setSmBusy] = useState(false);
    const [smQuery, setSmQuery] = useState(initial?.name || "");
    const [smId, setSmId] = useState("");
    const [smResults, setSmResults] = useState(null);
    const [smSeasons, setSmSeasons] = useState(null); // {sm_id, used_id, list}
    const [pasteText, setPasteText] = useState("");
    const [pasteBusy, setPasteBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const num = (v) => (v === "" || v == null ? null : Number(v));

    const applySmData = (d, pickName) => {
        const s = d.prefill || {};
        setF((prev) => {
            const next = { ...prev };
            Object.keys(EMPTY).forEach((k) => {
                if (s[k] !== undefined && s[k] !== null && s[k] !== "") next[k] = s[k];
            });
            if (!next.club && d.source_team) next.club = d.source_team;
            return next;
        });
        setSmSeasons({ sm_id: d.sm_id, used_id: d.season_id_used, list: d.seasons_available || [] });
        setAiNote({ confidence: 95, note: `SportMonks: ${pickName || s.name} (${d.source_team || "?"}), ${d.season_used || "?"}` });
        toast.success(`Načteno ze SportMonks: ${d.season_used || "poslední sezóna"}`);
    };

    const smSearch = async () => {
        const q = (smQuery || f.name || "").trim();
        if (q.length < 3) { toast.error("Zadej aspoň 3 znaky jména"); return; }
        setSmBusy(true);
        setSmResults(null);
        try {
            const found = await radarSmSearch(q);
            if (!found.results?.length) { toast.error("SportMonks hráče nenašel — zkus přesnější jméno"); return; }
            setSmResults(found.results);
        } catch (e) { toast.error(e.response?.data?.detail || "SportMonks hledání selhalo"); }
        finally { setSmBusy(false); }
    };

    const parseProfile = async () => {
        if (pasteText.trim().length < 40) { toast.error("Vlož delší text se statistikami"); return; }
        setPasteBusy(true);
        try {
            const d = await radarParseProfile(pasteText);
            applySmData(d, d.prefill?.name);
            setPasteText("");
        } catch (e) { toast.error(e.response?.data?.detail || "AI parsování selhalo"); }
        finally { setPasteBusy(false); }
    };

    const smImportById = async (id, seasonId, pickName) => {
        if (!id) { toast.error("Zadej SportMonks ID"); return; }
        setSmBusy(true);
        try {
            const d = await radarSmPlayer(id, seasonId);
            applySmData(d, pickName);
            setSmResults(null);
        } catch (e) { toast.error(e.response?.data?.detail || "SportMonks import selhal"); }
        finally { setSmBusy(false); }
    };

    const aiPrefill = async () => {
        if (!f.name) { toast.error("Nejdřív zadej jméno hráče"); return; }
        setAiBusy(true);
        try {
            const r = await axios.post(`${API}/prefill`, { name: f.name, club: f.club || "" });
            const s = r.data.suggestion || {};
            const next = { ...f };
            Object.keys(EMPTY).forEach((k) => {
                if (s[k] !== undefined && s[k] !== null && (next[k] === "" || next[k] == null)) next[k] = s[k];
            });
            setF(next);
            setAiNote({ confidence: s.confidence, note: s.source_note });
            toast.success(`AI návrh doplněn (jistota ${s.confidence ?? "?"}%) — zkontroluj čísla!`);
        } catch (e) { toast.error(e.response?.data?.detail || "AI návrh selhal"); }
        finally { setAiBusy(false); }
    };

    const submit = async () => {
        if (!f.name || !f.club) { toast.error("Jméno a klub jsou povinné"); return; }
        setBusy(true);
        const payload = { ...f, age: num(f.age), minutes: num(f.minutes), goals: num(f.goals), assists: num(f.assists),
            xg: num(f.xg), xa: num(f.xa), clean_sheets: num(f.clean_sheets), def_actions90: num(f.def_actions90), aerials90: num(f.aerials90),
            cards: num(f.cards), fee_m: num(f.fee_m), expected_price: num(f.expected_price), preseason_minutes: num(f.preseason_minutes) };
        try {
            if (initial?.id) await axios.put(`${API}/players/${initial.id}`, payload);
            else await axios.post(`${API}/players`, payload);
            toast.success("Uloženo a přepočítáno");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    return (
        <div className="border border-fa-mint/40 bg-[#0d0d0d] p-4 space-y-3" data-testid="radar-form">
            <div className="flex justify-between items-center">
                <p className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">{initial?.id ? "Upravit hráče" : "Nový hráč na radaru"}</p>
                <button type="button" onClick={onCancel} className="text-white/40 hover:text-white"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Field f={f} set={set} label="Jméno *" k="name" ph="João Neves" />
                <Field f={f} set={set} label="Klub v PL *" k="club" ph="Arsenal" />
                <Field f={f} set={set} label="Přirozená pozice" k="natural_position" ph="CM / box-to-box" />
                <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block">
                    <div>FPL pozice</div>
                    <select value={f.fpl_position} onChange={set("fpl_position")} className={inputCls} data-testid="radar-f-fpl_position">
                        {["GKP", "DEF", "MID", "FWD"].map((p) => <option key={p}>{p}</option>)}
                    </select>
                </label>
                <Field f={f} set={set} label="Věk" k="age" type="number" />
                <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block">
                    <div>Předchozí liga</div>
                    <select value={f.previous_league} onChange={set("previous_league")} className={inputCls} data-testid="radar-f-previous_league">
                        {LEAGUES.map((l) => <option key={l}>{l}</option>)}
                    </select>
                </label>
                <Field f={f} set={set} label="Minuty (min. sezóna)" k="minutes" type="number" />
                <Field f={f} set={set} label="Góly" k="goals" type="number" />
                <Field f={f} set={set} label="Asistence" k="assists" type="number" />
                <Field f={f} set={set} label="xG (sezóna)" k="xg" type="number" />
                <Field f={f} set={set} label="xA (sezóna)" k="xa" type="number" />
                <Field f={f} set={set} label="Čistá konta (sezóna)" k="clean_sheets" type="number" />
                <Field f={f} set={set} label="Def. akce /90 (tackles+int+clear)" k="def_actions90" type="number" />
                <Field f={f} set={set} label="Vzdušné souboje /90" k="aerials90" type="number" />
                <Field f={f} set={set} label="Karty (počet)" k="cards" type="number" />
                <Field f={f} set={set} label="Přestupová částka (M€)" k="fee_m" type="number" />
                <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block">
                    <div>Konkurence v týmu</div>
                    <select value={f.competition} onChange={set("competition")} className={inputCls} data-testid="radar-f-competition">
                        <option value="low">nízká</option><option value="medium">střední</option><option value="high">vysoká</option>
                    </select>
                </label>
                <Field f={f} set={set} label="Očekávaná FPL cena (£)" k="expected_price" type="number" />
                <Field f={f} set={set} label="Pre-season minuty" k="preseason_minutes" type="number" />
            </div>
            <textarea value={f.note} onChange={set("note")} rows={2} placeholder="Poznámka…" className={inputCls} data-testid="radar-f-note" />
            <div className="border border-fa-mint/25 bg-black/30 p-3 space-y-2" data-testid="radar-sm-panel">
                <p className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">SportMonks — načtení reálných statistik</p>
                <div className="flex gap-2 flex-wrap items-end">
                    <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block flex-1 min-w-[160px]">
                        <div>Hledat hráče podle jména</div>
                        <input value={smQuery} onChange={(e) => setSmQuery(e.target.value)}
                               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); smSearch(); } }}
                               placeholder="např. Gyökeres" className={inputCls} data-testid="radar-sm-query" />
                    </label>
                    <button type="button" onClick={smSearch} disabled={smBusy} data-testid="radar-sm-search-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-40">
                        {smBusy ? "…" : "Hledat"}
                    </button>
                    <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block w-32">
                        <div>Nebo SportMonks ID</div>
                        <input value={smId} onChange={(e) => setSmId(e.target.value)} type="number"
                               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); smImportById(smId); } }}
                               placeholder="194167" className={inputCls} data-testid="radar-sm-id" />
                    </label>
                    <button type="button" onClick={() => smImportById(smId)} disabled={smBusy || !smId} data-testid="radar-sm-id-import"
                            className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40">
                        Import ID
                    </button>
                </div>
                {smResults && (
                    <div className="space-y-1" data-testid="radar-sm-results">
                        {smResults.map((res) => (
                            <button key={res.id} type="button" onClick={() => smImportById(res.id, null, res.name)}
                                    data-testid={`radar-sm-result-${res.id}`}
                                    className="w-full flex items-center gap-2 border border-white/10 hover:border-fa-mint/60 px-2 py-1.5 text-left">
                                {res.image && <img src={res.image} alt="" className="w-6 h-6 rounded-full object-cover" />}
                                <span className="font-mono text-[11px] font-bold text-white/90">{res.name}</span>
                                <span className="font-mono text-[9px] text-white/45">{res.team || "?"} · {res.position || "?"}{res.birth_date ? ` · ${res.birth_date.slice(0, 4)}` : ""}</span>
                                <span className="ml-auto font-mono text-[8px] text-white/30">ID {res.id}</span>
                            </button>
                        ))}
                    </div>
                )}
                {smSeasons?.list?.length > 0 && (
                    <label className="font-mono text-[9px] uppercase tracking-widest text-white/70 space-y-0.5 block">
                        <div>Sezóna / soutěž (přepnutím se statistiky načtou znovu)</div>
                        <select value={smSeasons.used_id ?? ""} data-testid="radar-sm-season"
                                onChange={(e) => smImportById(smSeasons.sm_id, Number(e.target.value))}
                                className={inputCls}>
                            {smSeasons.list.map((s) => (
                                <option key={s.season_id} value={s.season_id}>
                                    {s.label}{s.minutes != null ? ` (${s.minutes} min)` : ""}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <div className="border-t border-white/10 pt-2 space-y-1.5">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-fa-cream">Nebo vlož profil (Sofascore, FBref…) — AI ho rozparsuje</p>
                    <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={3}
                              placeholder="Otevři hráče na Sofascore, označ a zkopíruj blok se statistikami sezóny (jméno, klub, soutěž, minuty, góly, xG…) a vlož sem. Nezapomeň, ať je v textu vidět soutěž a ročník, který chceš stáhnout."
                              className={`${inputCls} w-full resize-y`} data-testid="radar-paste-text" />
                    <button type="button" onClick={parseProfile} disabled={pasteBusy || pasteText.trim().length < 40}
                            data-testid="radar-paste-parse"
                            className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-violet/70 text-fa-cream hover:bg-fa-violet disabled:opacity-40">
                        {pasteBusy ? "AI analyzuje…" : "✦ Analyzovat AI"}
                    </button>
                </div>
            </div>
            {aiNote && (
                <p className="font-mono text-[9px] text-fa-yellow border-l-2 border-fa-yellow pl-2" data-testid="radar-ai-note">
                    AI návrh (jistota {aiNote.confidence ?? "?"}%): {aiNote.note} — čísla ověř, než uložíš.
                </p>
            )}
            <div className="flex gap-2">
                <button type="button" onClick={aiPrefill} disabled={aiBusy || !f.name} data-testid="radar-ai-prefill"
                        className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40">
                    {aiBusy ? "Navrhuji…" : "✦ AI návrh statistik"}
                </button>
                <button type="button" onClick={submit} disabled={busy} data-testid="radar-form-submit"
                        className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold hover:opacity-90 disabled:opacity-40">
                    {busy ? "Počítám…" : "Uložit a spočítat"}
                </button>
            </div>
        </div>
    );
}

function IncomingTransfers({ onPick }) {
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(true);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        axios.get(`${API}/incoming`).then((r) => setRows(r.data.transfers)).catch(() => setRows([]));
    }, []);

    if (!rows?.length) return null;
    const fresh = rows.filter((t) => !t.on_radar);
    const list = showAll ? rows : fresh.slice(0, 8);
    return (
        <div className="border border-fa-sky/30 bg-[#0d0d0d]" data-testid="radar-incoming">
            <button type="button" onClick={() => setOpen(!open)}
                    className="w-full flex items-center gap-2 p-3 font-mono text-[9px] uppercase tracking-widest text-fa-sky">
                Příchozí přestupy do PL ({fresh.length} mimo radar / {rows.length} celkem)
                {open ? <CaretUp size={11} className="ml-auto" /> : <CaretDown size={11} className="ml-auto" />}
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-1">
                    {list.map((t) => (
                        <div key={t.row_id} className="flex items-center gap-2 flex-wrap font-mono text-[10px] border-t border-white/5 py-1.5"
                             data-testid={`radar-incoming-${t.row_id}`}>
                            <span className="font-bold text-white/90">{t.player_name}</span>
                            <span className="text-white/40">{t.position}</span>
                            <span className="text-white/55">{t.from_team} → <span className="text-fa-mint">{t.to_team}</span></span>
                            {t.amount_eur ? <span className="text-white/40">€{(t.amount_eur / 1e6).toFixed(1)}M</span> : null}
                            <span className="text-white/30">{t.date}</span>
                            {t.fpl_relevant && <span className="text-[8px] uppercase text-fa-yellow border border-fa-yellow/40 px-1">FPL</span>}
                            {t.on_radar ? (
                                <span className="ml-auto flex items-center gap-1.5" data-testid={`radar-incoming-score-${t.row_id}`}>
                                    <span className={`font-bold tabular-nums ${(t.radar?.readiness ?? 0) >= 65 ? "text-fa-mint" : (t.radar?.readiness ?? 0) >= 45 ? "text-fa-yellow" : "text-red-300"}`}>
                                        {t.radar?.readiness ?? "—"}
                                    </span>
                                    {t.radar?.action && (
                                        <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 ${ACTION_STYLE[t.radar.action] || ""}`}>{t.radar.action}</span>
                                    )}
                                </span>
                            ) : (
                                <button type="button" onClick={() => onPick(t)} data-testid={`radar-incoming-add-${t.row_id}`}
                                        className="ml-auto text-[9px] uppercase tracking-widest px-2 py-1 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black">
                                    + na radar
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={() => setShowAll(!showAll)}
                            className="font-mono text-[9px] text-white/40 hover:text-white/70 pt-1">
                        {showAll ? "Zobrazit méně" : `Zobrazit vše (${rows.length})`}
                    </button>
                </div>
            )}
        </div>
    );
}

function PlayerCard({ p, isAdmin, onEdit, onDelete }) {
    const [open, setOpen] = useState(false);
    const s = p.scores || {};
    const prof = s.profile || {};
    const fx = p.fixtures || {};
    return (
        <div className="border border-white/10 bg-[#0d0d0d]" data-testid={`radar-card-${p.id}`}>
            <div className="p-3 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-white/[0.02]" onClick={() => setOpen(!open)}>
                <div className="text-center w-14">
                    <div className={`font-display font-black text-2xl tabular-nums ${s.readiness >= 65 ? "text-fa-mint" : s.readiness >= 45 ? "text-fa-yellow" : "text-red-300"}`}>{s.readiness ?? "—"}</div>
                    <div className="font-mono text-[7px] uppercase text-white/35">readiness</div>
                </div>
                <div className="flex-1 min-w-[180px]">
                    <div className="font-display font-black text-lg">{p.name}
                        <span className="font-mono text-[9px] text-white/40 ml-2">{p.club} · {p.fpl_position} · £{p.expected_price ?? "?"}{p.fpl_price_confirmed && <span className="text-fa-mint" title="Cena potvrzena z FPL API"> ✓FPL</span>}</span>
                        {p.arrived && (
                            <Link to={`/players/${p.fpl_id}`} onClick={(e) => e.stopPropagation()}
                                  data-testid={`radar-arrived-${p.id}`}
                                  className="font-mono text-[8px] uppercase tracking-widest ml-2 px-1.5 py-0.5 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black">
                                V FPL · £{p.fpl_price} →
                            </Link>
                        )}
                    </div>
                    <div className="font-mono text-[9px] text-white/45">{p.previous_league} · {p.age ? `${p.age} let` : ""} {p.fee_m ? `· €${p.fee_m}M` : ""}</div>
                </div>
                <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${ACTION_STYLE[s.action] || ""}`} data-testid={`radar-action-${p.id}`}>{s.action}</span>
                <span className={`font-mono text-[9px] uppercase ${ROLE_STYLE[p.role] || "text-white/50"}`}>{p.role}</span>
                <div className="w-28">
                    <Bar label="Minuty" value={s.minutes_probability ?? 0} color={s.minutes_probability >= 65 ? "bg-fa-mint" : "bg-fa-yellow"} />
                </div>
                <span className={`font-mono text-[9px] ${PRICE_STYLE[s.price_sensitivity] || ""}`}>{PRICE_LABEL[s.price_sensitivity] || ""}</span>
                {isAdmin && (
                    <span className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={onEdit} className="text-fa-sky hover:opacity-80" data-testid={`radar-edit-${p.id}`}><PencilSimple size={13} /></button>
                        <button type="button" onClick={onDelete} className="text-red-300/70 hover:text-red-300" data-testid={`radar-delete-${p.id}`}><Trash size={13} /></button>
                    </span>
                )}
                {open ? <CaretUp size={12} className="text-fa-sky" /> : <CaretDown size={12} className="text-fa-sky" />}
            </div>
            {open && (
                <div className="border-t border-white/10 p-3 grid md:grid-cols-3 gap-4" data-testid={`radar-detail-${p.id}`}>
                    <div className="space-y-1.5">
                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40">FPL scoring profil</p>
                        <Bar label="Útočná hrozba" value={prof.attacking_threat ?? 0} />
                        <Bar label="Clean sheet potenciál" value={prof.clean_sheet_potential ?? 0} color="bg-fa-sky" />
                        <Bar label="Defenzivní příspěvek (DC)" value={prof.defensive_contribution ?? 0} color="bg-fa-sky" />
                        <Bar label="Bonusový potenciál" value={prof.bonus_potential ?? 0} color="bg-fa-yellow" />
                        <Bar label="Riziko karet" value={prof.card_risk ?? 0} color="bg-red-400" />
                        <div className="font-mono text-[9px] text-white/50 pt-1">
                            xG90 {s.per90?.xg} · xA90 {s.per90?.xa} · liga ×{s.league_factor} · proj. {s.projected_ppg} PPG
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40">Rizika & jistota</p>
                        <Bar label="Nailedness confidence" value={s.nailedness ?? 0} />
                        <Bar label="Adaptation risk" value={s.adaptation_risk ?? 0} color="bg-red-400" />
                        <Bar label="Pravděpodobnost minut" value={s.minutes_probability ?? 0} />
                        <p className="font-mono text-[9px] pt-1">
                            Cena: <span className={PRICE_STYLE[s.price_sensitivity]}>{PRICE_LABEL[s.price_sensitivity]}</span>
                            <span className="text-white/40"> · role: </span><span className={ROLE_STYLE[p.role]}>{p.role}</span>
                        </p>
                        {p.note && <p className="font-mono text-[9px] text-white/50 border-l-2 border-white/20 pl-2">{p.note}</p>}
                    </div>
                    <div className="space-y-2">
                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40">Fixtures & rotace ({p.club})</p>
                        {(fx.best_blocks || []).map((b, i) => (
                            <div key={i} className="font-mono text-[9px] text-white/70">
                                <span className="text-fa-mint font-bold">GW{b.gw_from}–{b.gw_to}</span>
                                <span className="text-white/40"> Ø FDR {b.avg_fdr} · </span>
                                {b.fixtures.map((f) => `${f.opp}${f.home ? "(H)" : "(A)"}`).join(" ")}
                            </div>
                        ))}
                        {fx.best_rotation_pair && (
                            <p className="font-mono text-[9px] text-fa-sky">
                                Nejlepší rotační partner: <strong>{fx.best_rotation_pair.partner}</strong> · Ø {fx.best_rotation_pair.avg_fdr} · {fx.best_rotation_pair.green_weeks}/{fx.best_rotation_pair.n_gws} green (GW1–10)
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function IncomingRadar() {
    const { user } = useAuth();
    const isAdmin = !!user?.is_admin;
    const [players, setPlayers] = useState(null);
    const [editing, setEditing] = useState(null); // null | {} (new) | player
    const [busy, setBusy] = useState(false);

    const load = () => axios.get(`${API}/players`).then((r) => setPlayers(r.data.players)).catch(() => setPlayers([]));
    useEffect(() => { document.title = "Incoming Player Radar | Fantasy Academy"; load(); }, []);

    const recompute = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/recompute`);
            toast.success(`Přepočítáno ${r.data.updated} hráčů · FPL ceny nalezeny: ${r.data.fpl_prices_found}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Přepočet selhal"); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        await axios.delete(`${API}/players/${id}`).catch(() => {});
        load();
    };

    return (
        <AppShell>
            <div className="max-w-5xl mx-auto space-y-4" data-testid="radar-page">
                <header className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream flex items-center gap-1.5"><RadarIcon size={12} /> Fantasy Academy</div>
                        <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">Incoming Player Radar</h1>
                        <p className="text-white/50 text-sm mt-1 max-w-2xl">Hráči nově přicházející do Premier League bez FPL historie — převedení do FPL scoring profilu přes league adjustment, minutes model a adaptation risk.</p>
                    </div>
                    {isAdmin && (
                        <div className="flex gap-2">
                            <button type="button" onClick={recompute} disabled={busy} data-testid="radar-recompute-btn"
                                    className="font-mono text-[9px] uppercase tracking-widest px-3 py-2 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40">
                                <ArrowsClockwise size={11} className={`inline mb-0.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Přepočítat + FPL ceny
                            </button>
                            <button type="button" onClick={() => setEditing({})} data-testid="radar-add-btn"
                                    className="font-mono text-[9px] uppercase tracking-widest px-3 py-2 bg-fa-mint text-black font-bold hover:opacity-90">
                                <Plus size={11} className="inline mb-0.5 mr-1" /> Přidat hráče
                            </button>
                        </div>
                    )}
                </header>

                {isAdmin && editing === null && (
                    <div className="flex gap-2 flex-wrap" data-testid="radar-dedupe-bar">
                        <button type="button" data-testid="radar-dedupe-btn"
                                onClick={async () => {
                                    try {
                                        const prev = await axios.post(`${API}/radar/dedupe?apply=false`);
                                        const m = prev.data.matched || [];
                                        if (!m.length) { toast.info("Žádné duplicity s FPL databází nenalezeny"); return; }
                                        if (!window.confirm(`Nalezeno ${m.length} shod s FPL databází:\n${m.map((x) => `• ${x.radar_name} → ${x.fpl_name} (${x.fpl_team}, £${x.fpl_price})`).join("\n")}\n\nSloučit?`)) return;
                                        await axios.post(`${API}/radar/dedupe?apply=true`);
                                        toast.success(`Sloučeno ${m.length} hráčů s FPL databází`);
                                        load();
                                    } catch (e) { toast.error(e.response?.data?.detail || "Sloučení selhalo"); }
                                }}
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black">
                            ⇄ Sloučit duplicity s FPL databází
                        </button>
                        <button type="button" data-testid="radar-delete-merged-btn"
                                onClick={async () => {
                                    if (!window.confirm("Smazat všechny sloučené radar hráče (jsou už v FPL databázi)?")) return;
                                    try {
                                        const r = await axios.delete(`${API}/radar/merged`);
                                        toast.success(`Smazáno ${r.data.deleted} sloučených hráčů z radaru`);
                                        load();
                                    } catch { toast.error("Mazání selhalo"); }
                                }}
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/50 hover:text-red-300 hover:border-red-400/50">
                            Smazat sloučené
                        </button>
                    </div>
                )}

                {isAdmin && editing === null && (
                    <IncomingTransfers onPick={(t) => {
                        const posMap = { GKP: "GKP", GK: "GKP", DEF: "DEF", MID: "MID", FWD: "FWD", ATT: "FWD" };
                        setEditing({
                            name: t.player_name, club: t.to_team,
                            fpl_position: posMap[(t.position || "").toUpperCase()] || "MID",
                            natural_position: t.position || "",
                            fee_m: t.amount_eur ? Math.round(t.amount_eur / 1e5) / 10 : "",
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }} />
                )}

                {editing !== null && (
                    <RadarForm initial={editing.id ? editing : editing.name ? editing : null}
                               onDone={() => { setEditing(null); load(); }}
                               onCancel={() => setEditing(null)} />
                )}

                {players === null && <p className="font-mono text-[11px] text-white/40">Načítám…</p>}
                {players?.length === 0 && (
                    <p className="font-mono text-[11px] text-white/40 border border-white/10 bg-[#0d0d0d] p-6" data-testid="radar-empty">
                        Radar je zatím prázdný.{isAdmin ? " Přidej prvního hráče tlačítkem výše." : ""}
                    </p>
                )}
                <div className="space-y-2">
                    {(players || []).map((p) => (
                        <PlayerCard key={p.id} p={p} isAdmin={isAdmin}
                                    onEdit={() => setEditing(p)} onDelete={() => remove(p.id)} />
                    ))}
                </div>
                {players?.length > 0 && (
                    <p className="font-mono text-[9px] text-white/35">
                        Top 30 dle Readiness tvoří „radar pool" (API /api/radar/pool) · skóre se přepočítává při každé úpravě a tlačítkem „Přepočítat" (načte FPL ceny po launchi + pre-season minuty zohledníš úpravou hráče).
                    </p>
                )}
            </div>
        </AppShell>
    );
}
